'use strict';
const _includes = require('lodash/includes');
const _assign = require('lodash/assign');
const _clone = require('lodash/clone');
const _remove = require('lodash/remove');
const Fingerprint2 = require('fingerprintjs2');
const cookie = require('js-cookie');
const dom = require('@nymag/dom');
const gtmEl = dom.find('.gtm');
const siteShortKey = gtmEl && gtmEl.getAttribute('data-site-short-key');

const localStorage = window.localStorage;
const uriAttr = 'data-uri';
const visitCountKey = `'visitServiceCount'_${siteShortKey}`;
const previousActionTimestampKey = `'visitServicePreviousTimestamp'_${siteShortKey}`;
const firstVisitTimestampKey = `'visitServiceFirstVisitTimestamp'_${siteShortKey}`;
const visitStartTimestampKey = `'visitServiceVisitStartTimestamp'_${siteShortKey}`;
const initialReferrerKey = `'visitServiceInitialRefferer'_${siteShortKey}`;
const visitDatesKey = `'visitDates'_${siteShortKey}`;
const visitUTMParamsKey = `'visitServiceUTMs'_${siteShortKey}`;
const clientIdCookieKey = 'nyma'; // set on all non-clay page
const maxVisitDuration = 1000 * 60 * 60 * 24; // a visit can not last more than 24 hour
const sessionTTL = 1000 * 60 * 30; // keep session open for 30 minutes after last even
const visitDatesLimit = 30; // number of days over which to track dates of visit
let publicState = {};
let publicStateIsReady = false;
let onceReadyQueue = [];
const state = Object.create(Object.prototype, {
  /**
   * runs all callbacks in the queue with the current value of clientId
   * @private
   */
  _processQ: {
    value: function () {
      var qClone = this._q.slice(0),
        l = qClone.length,
        i;

      this._q = [];
      for (i = 0; i < l; i++) {
        qClone[i].call(null, this._clientId);
      }
    },
  },
  _q: {
    writable: true,
    value: [],
  },
  _clientId: {
    writable: true,
    value: null,
  },
  clientId: {
    /**
     * attempts to get clientId as stored in private `_clientId`
     * @returns {state._clientId}
     */
    get: function () {
      return this._clientId;
    },
    /**
     * sets the clientId in state and cookie, then processes queue of callbacks
     * @param {string} val
     */
    set: function (val) {
      if (val) {
        this._clientId = val;
      }
    },
  },
  /**
   * the clientId may need to be retrieved through an async process
   * this method will call the callback once the clientId is ready
   * @param {Function} callback
   */
  onceClientIdIsReady: {
    value: function (callback) {
      this._clientId
        ? callback.call(null, this._clientId)
        : this._q.push(callback);
    },
  },
  isNewVisit: {
    writable: true,
    value: false,
  },
});

const TRACKING_STORAGE_KEYS = [
  visitCountKey,
  previousActionTimestampKey,
  firstVisitTimestampKey,
  visitStartTimestampKey,
  initialReferrerKey,
  visitDatesKey,
  visitUTMParamsKey,
];

/**
 * @param {string|number} [val]
 * @returns {number} defaults to zero if number can not be parsed
 */
function toNumber(val) {
  return parseInt(val, 10) || 0; // defaults to zero
}
/**
 *
 * @param {string} key
 * @returns {number}
 */
function getNumberFromLocalStorage(key) {
  return toNumber(localStorage.getItem(key)); // defaults to zero
}

/**
 * get visit count from localStorage
 * @returns {number}
 */
function getVisitCount() {
  return getNumberFromLocalStorage(visitCountKey);
}

/**
 * get visit count from localStorage
 * @returns {number}
 */
function getFirstVisitTimestamp() {
  return getNumberFromLocalStorage(firstVisitTimestampKey);
}

/**
 * update the visitCount key in localStorage
 */
function incrementVisitCount() {
  try {
    // the visit count is incremented on every new user session, and not every new visit
    // gets pushed into the visitDates key.
    localStorage.setItem(visitCountKey, getVisitCount() + 1);
  } catch (e) {
    // the browser failed to setItem, likely due to Private Browsing mode. We don't need to take any action
  }
}

/**
 * parse the clientId which contains the first visit timestamp
 * @param {string} clientId
 * @returns {number}
 */
function clientIdToFirstVisitTimestamp(clientId) {
  return toNumber(clientId.substr(clientId.indexOf('.') + 1)); // assumes that timestamp is at end of clientId
}

/**
 *
 * @param {string} clientId
 * @returns {number}
 */
function ensureFirstVisitTimestamp(clientId) {
  var firstVisitTimestamp = getFirstVisitTimestamp();

  if (!firstVisitTimestamp) {
    firstVisitTimestamp = clientIdToFirstVisitTimestamp(clientId); // could be 0 if clientId is malformed
    try {
      localStorage.setItem(firstVisitTimestampKey, firstVisitTimestamp);
    } catch (e) {
      // the browser failed to setItem, likely due to Private Browsing mode. We don't need to take any action
    }
  }
  return firstVisitTimestamp;
}

/**
 *
 * @param {number} timestamp
 */
function setPreviousActionTimestamp(timestamp) {
  try {
    localStorage.setItem(previousActionTimestampKey, timestamp);
  } catch (e) {
    // the browser failed to setItem, likely due to Private Browsing mode. We don't need to take any action
  }
}

/**
 * @param {number} timestamp
 */
function setVisitStartTimestamp(timestamp) {
  try {
    localStorage.setItem(visitStartTimestampKey, timestamp);
  } catch (e) {
    // the browser failed to setItem, likely due to Private Browsing mode. We don't need to take any action
  }
}

/**
 * Checks if more than the `sessionTTL` has elapsed since the previous action
 * @param {number} currentTimestamp
 * @returns {boolean}
 */
function isNewVisit(currentTimestamp) {
  var sessionExpired =
      currentTimestamp - sessionTTL >
      getNumberFromLocalStorage(previousActionTimestampKey),
    visitDurationExpired =
      currentTimestamp - maxVisitDuration >
      getNumberFromLocalStorage(visitStartTimestampKey);

  return sessionExpired || visitDurationExpired;
}

/**
 * @param {number} currentTimestamp
 */
function updateNewVisitState(currentTimestamp) {
  state.isNewVisit = isNewVisit(currentTimestamp);
}

/**
 * Extend visit whenever an "action" is taken to keep the session alive
 * This should be triggered when any user action is detected
 * Updates the previous action timestamp if the visit session has not timed out
 */
function extendVisit() {
  var currentTimestamp = Date.now(),
    currentVisitIsAlive = !isNewVisit(currentTimestamp);

  if (currentVisitIsAlive) {
    // update timestamp only if session still alive
    setPreviousActionTimestamp(currentTimestamp);
  }
  // todo: in the future we may want to add a "visit" event that other components could listen for
  // for simplicity we are only triggering a new visit/page view when a page is loaded
}

/**
 * gets clientId from cookie or creates a new one, then executes a callback
 * @param {number} currentTimestamp
 * @param {function} callback
 * @returns {void}
 */
function ensureClientId(currentTimestamp, callback) {
  let clientId = cookie.get(clientIdCookieKey);

  if (clientId) {
    state.clientId = clientId;
    return callback(clientId);
  } else {
    new Fingerprint2({
      // Fingerprint2 npm module added with gulp
      excludeJsFonts: true,
      excludeFlashFonts: true,
      excludeCanvas: true,
      excludeWebGL: true,
      excludePixelRatio: true,
    }).get(function (fingerprint) {
      clientId = fingerprint + '.' + currentTimestamp;
      state.clientId = clientId;
      callback(clientId);
    });
  }
}

/**
 *
 * @returns {string|null}
 */
function getPageUri() {
  var el = document.querySelector('[' + uriAttr + '*="/_pages/"]');

  return el && el.getAttribute(uriAttr);
}

/**
 * returns an object with key value pairs parsed from query string
 * @param {array} keys
 * @returns {object}
 */
function getQueryParamsObject(keys) {
  const params = new URLSearchParams(window.location.search);
  const result = {};

  if (!Array.isArray(keys) || keys.length === 0) return result;

  for (const key of keys) {
    if (params.has(key)) {
      result[key] = params.get(key);
    }
  }

  return result;
}

/**
 * Gets the UTM params if available as query params and stores them in local storage for later page visits
 * @param {number} currentTimestamp
 * @returns {object}
 */
function getUtmParams(currentTimestamp) {
  const keys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];
  const utmParamsInURL = getQueryParamsObject(keys);

  if (Object.keys(utmParamsInURL).length > 0) {
    try {
      localStorage.setItem(visitUTMParamsKey, JSON.stringify(utmParamsInURL));
    } catch (e) {}

    return utmParamsInURL;
  }

  // Check if a new visit has started
  const newVisit = isNewVisit(currentTimestamp);

  // If it's a new visit and no UTM params in the URL, clear any existing ones
  if (newVisit) {
    try {
      localStorage.removeItem(visitUTMParamsKey);
    } catch (e) {}

    return {};
  }

  // it's not a new visit, retrieve the UTM params from local storage
  const utmParamsInStorage = localStorage.getItem(visitUTMParamsKey);
  return utmParamsInStorage
    ? JSON.parse(utmParamsInStorage)
    : {};
}

/**
 * derived from mixpanel so that our stats are consistent with page view props
 * source: https://github.com/mixpanel/mixpanel-js/blob/f8fe5b29ea90ab89df21e8db53c5fbaf0ebf8174/src/utils.js#L1376
 * @param {string} [userAgent]
 * @param {string} [vendor]
 * @param {object} [opera]
 * @returns {string}
 */
function getBrowserName(userAgent, vendor, opera) {
  // disabling linting to keep code in-line with mixpanel as closely as possible
  /* eslint complexity: [2, 20] */
  // order matters
  if (opera || _includes(userAgent, ' OPR/')) {
    if (_includes(userAgent, 'Mini')) {
      return 'Opera Mini';
    }
    return 'Opera';
  } else if (/(BlackBerry|PlayBook|BB10)/i.test(userAgent)) {
    return 'BlackBerry';
  } else if (
    _includes(userAgent, 'IEMobile') ||
    _includes(userAgent, 'WPDesktop')
  ) {
    return 'Internet Explorer Mobile';
  } else if (_includes(userAgent, 'Edge')) {
    return 'Microsoft Edge';
  } else if (_includes(userAgent, 'FBIOS')) {
    return 'Facebook Mobile';
  } else if (_includes(userAgent, 'Chrome')) {
    return 'Chrome';
  } else if (_includes(userAgent, 'CriOS')) {
    return 'Chrome iOS';
  } else if (_includes(userAgent, 'FxiOS')) {
    return 'Firefox iOS';
  } else if (_includes(vendor, 'Apple')) {
    if (_includes(userAgent, 'Mobile')) {
      return 'Mobile Safari';
    }
    return 'Safari';
  } else if (_includes(userAgent, 'Android')) {
    return 'Android Mobile';
  } else if (_includes(userAgent, 'Konqueror')) {
    return 'Konqueror';
  } else if (_includes(userAgent, 'Firefox')) {
    return 'Firefox';
  } else if (_includes(userAgent, 'MSIE') || _includes(userAgent, 'Trident/')) {
    return 'Internet Explorer';
  } else if (_includes(userAgent, 'Gecko')) {
    return 'Mozilla';
  }
  return '';
}

/**
 * derived from mixpanel so that our stats are consistent with page view props
 * source: https://github.com/mixpanel/mixpanel-js/blob/f8fe5b29ea90ab89df21e8db53c5fbaf0ebf8174/src/utils.js#L1442
 * @param {string} browserName
 * @param {string} userAgent
 * @returns {Number|null}
 */
function browserNameToVersion(browserName, userAgent) {
  var browserRegexMap = {
      'Internet Explorer Mobile': /rv:(\d+(\.\d+)?)/,
      'Microsoft Edge': /Edge\/(\d+(\.\d+)?)/,
      Chrome: /Chrome\/(\d+(\.\d+)?)/,
      'Chrome iOS': /CriOS\/(\d+(\.\d+)?)/,
      Safari: /Version\/(\d+(\.\d+)?)/,
      'Mobile Safari': /Version\/(\d+(\.\d+)?)/,
      Opera: /(Opera|OPR)\/(\d+(\.\d+)?)/,
      Firefox: /Firefox\/(\d+(\.\d+)?)/,
      'Firefox iOS': /FxiOS\/(\d+(\.\d+)?)/,
      Konqueror: /Konqueror:(\d+(\.\d+)?)/,
      BlackBerry: /BlackBerry (\d+(\.\d+)?)/,
      'Android Mobile': /android\s(\d+(\.\d+)?)/,
      'Internet Explorer': /(rv:|MSIE )(\d+(\.\d+)?)/,
      Mozilla: /rv:(\d+(\.\d+)?)/,
    },
    regex = browserRegexMap[browserName],
    matches = regex && userAgent.match(regex);

  if (matches) {
    return parseFloat(matches[matches.length - 2]);
  }
  return null;
}

/**
 * derived from mixpanel so that our stats are consistent with page view props
 * source: https://github.com/mixpanel/mixpanel-js/blob/f8fe5b29ea90ab89df21e8db53c5fbaf0ebf8174/src/utils.js#L1451
 * @param {string} userAgent
 * @returns {string}
 */
function userAgentToOs(userAgent) {
  // disabling linting to keep code in-line with mixpanel as closely as possible
  /* eslint complexity: [2, 20] */
  if (/Windows/i.test(userAgent)) {
    if (/Phone/.test(userAgent) || /WPDesktop/.test(userAgent)) {
      return 'Windows Phone';
    }
    return 'Windows';
  } else if (/(iPhone|iPad|iPod)/.test(userAgent)) {
    return 'iOS';
  } else if (/Android/.test(userAgent)) {
    return 'Android';
  } else if (/(BlackBerry|PlayBook|BB10)/i.test(userAgent)) {
    return 'BlackBerry';
  } else if (/Mac/i.test(userAgent)) {
    return 'Mac OS X';
  } else if (/Linux/.test(userAgent)) {
    return 'Linux';
  }
  return '';
}

/**
 * returns an object with information about the viewing environment
 * @returns {object}
 */
function getBrowserInfo() {
  var navigator = window.navigator,
    userAgent = navigator.userAgent,
    vendor = navigator.vendor,
    browserName = getBrowserName(userAgent, vendor, window.opera);

  return {
    browser: browserName,
    browserVersion: browserNameToVersion(browserName, userAgent),
    os: userAgentToOs(userAgent),
  };
}

/**
 * These are the values accessible through `getPublicStateOnceReady`
 * @param {string} clientId
 * @param {number} currentTimestamp
 */
function setPublicState(clientId, currentTimestamp) {
  publicState = _assign(
    {
      clientId: clientId,
      currentUrl: window.location.href,
      firstVisitTimestamp: getFirstVisitTimestamp(),
      initialReferrer: localStorage.getItem(initialReferrerKey),
      isNewVisit: state.isNewVisit,
      pageUri: getPageUri(),
      referrer: document.referrer,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      timestamp: currentTimestamp,
      visitCount: getVisitCount(),
      userLoyalty: getUserLoyaltyLevel(),
    },
    getBrowserInfo(),
    getUtmParams(currentTimestamp) // could do server-side
  );

  publicStateIsReady = true;
}

/**
 * check that publicState has been set before executing callbacks
 * @param {Function} callback
 */
function getPublicStateOnceReady(callback) {
  if (publicStateIsReady) {
    callback(_clone(publicState));
  } else {
    onceReadyQueue.push(callback);
  }
}

function executeOnceReadyQueue() {
  onceReadyQueue.forEach((callback) => callback(_clone(publicState)));
  onceReadyQueue = [];
}

/**
 * maintain array of dates of a user's visits
 */
function updateVisitDates() {
  var visitDatesStr = localStorage.getItem(visitDatesKey) || '',
    visitDates = visitDatesStr.split(','),
    visitDatesLimitMilliseconds = visitDatesLimit * 24 * 60 * 60 * 1000, // visitDatesLimit in milliseconds
    thresholdTimeSinceLastVisit = 24 * 60 * 60 * 1000, // 24 hours in milliseconds
    currentTime = Date.now();

  // remove dates older than threshold
  _remove(visitDates, function (visitDate) {
    return currentTime - visitDate > visitDatesLimitMilliseconds;
  });

  // add current time if at least 24 hours have elapsed since the visit
  if (
    visitDates.length === 0 ||
    currentTime - visitDates[0] >= thresholdTimeSinceLastVisit
  ) {
    visitDates.unshift(currentTime);
  }

  localStorage.setItem(visitDatesKey, visitDates.join(','));
}

/**
 * return user loyalty level
 * @returns {string}
 */
function getUserLoyaltyLevel() {
  var visitDatesStr = localStorage.getItem(visitDatesKey) || '',
    visitDates = visitDatesStr.split(',');

  if (visitDates.length <= 1) {
    return 'new';
  } else if (visitDates.length < 4) {
    return 'return';
  } else {
    return 'loyal';
  }
}

/**
 * This should be triggered only on load as that is when we track new visits.
 */
function init() {
  var currentTimestamp = Date.now();

  state.clientId = null;
  setPublicState(state.clientId, currentTimestamp);

  executeOnceReadyQueue();

  window.document.addEventListener('click', extendVisit);
}

/**
 * This function activates the full tracking capabilities of the visit service.
 * It should be called after user consent is given.
 */
function activateTracking() {
  const currentTimestamp = Date.now();

  ensureClientId(currentTimestamp, function (clientId) {
    cookie.set(clientIdCookieKey, clientId, { expires: 365 * 5 });
    state._processQ();
    ensureFirstVisitTimestamp(clientId);
    updateNewVisitState(currentTimestamp);

    if (state.isNewVisit) {
      setVisitStartTimestamp(currentTimestamp);
      incrementVisitCount();
      updateVisitDates();
      try {
        localStorage.setItem(initialReferrerKey, document.referrer);
      } catch (e) {
        // can't set to localstorage
      }
    }
    setPreviousActionTimestamp(currentTimestamp);
    setPublicState(clientId, currentTimestamp);
    executeOnceReadyQueue();
  });
}

/**
 * This function deactivates the full tracking capabilities of the visit service.
 * Called in onetrust OptanonWrapper
 */
function deactivateTracking() {
  // remove the nyma cookie
  cookie.remove(clientIdCookieKey);

  // clear all tracking-related localStorage items
  TRACKING_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // ignore errors
    }
  });

  // Reset internal state in case the service is still used on the page
  state.clientId = null;
  state.isNewVisit = false;
}

// init on load
init();

module.exports.onceReady = getPublicStateOnceReady;
module.exports.getQueryParamsObject = getQueryParamsObject;
module.exports.getBrowserInfo = getBrowserInfo;
module.exports.activateTracking = activateTracking;
module.exports.deactivateTracking = deactivateTracking;
